import { ReactNode, useMemo } from "react"
import {
  Block,
  BlockProps,
  ButtonIcon,
  ChevronLeft,
  ChevronRight,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { useDraggableContainer } from "#/shared/hooks"

type DraggableContainerProps = BlockProps & {
  header?: ReactNode
  hasControls?: boolean
}

type DraggableContainerControlVariant = "backward" | "forward"

type DraggableContainerControlProps = {
  onClick: (variant: DraggableContainerControlVariant) => void
  variant: DraggableContainerControlVariant
}

function DraggableContainerControl({
  onClick,
  variant,
}: DraggableContainerControlProps) {
  const [, theme] = useStyletron()

  return (
    <ButtonIcon
      size="small"
      onClick={() => onClick(variant)}
      overrides={{
        BaseButton: {
          style: () => ({
            width: "36px",
            height: "36px",
            borderRadius: "100%",
          }),
        },
      }}
    >
      {variant === "backward" ? (
        <ChevronLeft size={theme.sizing.scale600} />
      ) : (
        <ChevronRight size={theme.sizing.scale600} />
      )}
    </ButtonIcon>
  )
}

export default function DraggableContainer({
  header,
  hasControls,
  children,
  ...props
}: DraggableContainerProps) {
  const [css, theme] = useStyletron()

  const {
    scrollContainerRef,
    isDragging,
    isScrollable,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleButtonClickMove,
  } = useDraggableContainer()

  const cursor = useMemo(() => {
    if (!isScrollable) return "default"

    return isDragging ? "grabbing" : "grab"
  }, [isScrollable, isDragging])

  return (
    <>
      {(header || hasControls) && (
        <Block
          className={css({
            marginBottom: theme.sizing.scale700,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "100%",
          })}
        >
          <Block>{header}</Block>
          {hasControls && isScrollable && (
            <Block
              className={css({ display: "flex", gap: theme.sizing.scale500 })}
            >
              <DraggableContainerControl
                variant="backward"
                onClick={handleButtonClickMove}
              />
              <DraggableContainerControl
                variant="forward"
                onClick={handleButtonClickMove}
              />
            </Block>
          )}
        </Block>
      )}
      <Block
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseUp}
        onMouseUp={handleMouseUp}
        className={css({
          cursor,
          overflowX: "scroll",
          userSelect: "none",
          WebkitOverflowScrolling: "touch",
          width: "100%",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        })}
        {...props}
      >
        {children}
      </Block>
    </>
  )
}
